import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { computeBlogPretextLayout } from '../../lib/blog/blogPretextLayout';

type Props = {
  content: string;
};

/**
 * Desktop layout: Pretext line breaking + absolute lines with float-left image obstacles
 * (same idea as chenglou.me / Pretext demos).
 */
export function BlogPostPretextArticle({ content }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(800);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setWidth(el.clientWidth || 800);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { lines, images, height } = useMemo(
    () => computeBlogPretextLayout(content, width),
    [content, width]
  );

  return (
    <div
      ref={ref}
      className="relative w-full text-content/80 select-text"
      style={{ minHeight: height }}
    >
      {images.map((img) => (
        <img
          key={img.key}
          src={img.src}
          alt={img.alt}
          className="absolute rounded-lg shadow-sm object-cover"
          style={{
            left: img.left,
            top: img.top,
            width: img.width,
            height: img.height,
          }}
          loading="lazy"
          decoding="async"
        />
      ))}
      {lines.map((line) => (
        <span
          key={line.key}
          className={
            line.kind === 'heading'
              ? 'text-content'
              : line.kind === 'quote'
                ? 'italic text-content/80'
                : 'text-content/80'
          }
          style={{
            position: 'absolute',
            left: line.left,
            top: line.top,
            font: line.font,
            whiteSpace: 'pre',
          }}
        >
          {line.text}
        </span>
      ))}
    </div>
  );
}
